import { Injectable, Provider } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from 'app/services/auth/auth.service';
import { HeaderBuilderService } from 'app/shared/services/header-builder/header-builder.service';

@Injectable()
export class AccessTokenInterceptor implements HttpInterceptor {
  constructor(
    private readonly _authService: AuthService,
    private readonly _headerBuilder: HeaderBuilderService
  ) {}

  private addToken(req: HttpRequest<any>): HttpRequest<any> {
    if (!this._headerBuilder.hasValidToken()) {
      return req;
    }

    const headers = this._headerBuilder.buildHeaders();
    return req.clone({ setHeaders: headers });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this._authService.isLoggedIn()) {
      // A still-valid refresh token can renew an expired session. Refresh proactively and send with a
      // fresh token, rather than firing an unauthenticated request that 401s and bounces the user to
      // /login — the cause of the DEO AI Assist sidebar vanishing on intermittent token expiry (PLAN-2200).
      if (this._authService.isTokenExpired()) {
        return this.refreshAndSend(req, next, 30);
      }
      return next.handle(req);
    }

    return next.handle(this.addToken(req)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && this._authService.keycloak?.token) {
          // Access token was rejected mid-flight — refresh and retry once before giving up.
          return this.refreshAndSend(req, next, 5, error);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Refreshes the token and, on success, sends the request with a fresh Authorization header.
   * On refresh failure the session is expired. Errors from the retried request itself are left to
   * propagate normally — only a failed refresh triggers logout.
   */
  private refreshAndSend(req: HttpRequest<any>, next: HttpHandler, minValidity: number, fallbackError?: HttpErrorResponse): Observable<HttpEvent<any>> {
    return from(this._authService.refreshToken(minValidity)).pipe(
      catchError(err => {
        console.warn('[AccessTokenInterceptor] Error during token refresh', err);
        return of(false);
      }),
      switchMap((refreshed: boolean) => {
        if (refreshed) {
          console.log('[AccessTokenInterceptor] Token refreshed, sending request');
          return next.handle(this.addToken(req));
        }
        console.warn('[AccessTokenInterceptor] Token refresh failed, logging out');
        this._authService.expireSession();
        return throwError(() => fallbackError ?? new HttpErrorResponse({ status: 401 }));
      })
    );
  }
}

export const accessTokenInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AccessTokenInterceptor,
  multi: true
};
